import {useUserContext} from "@hosttools/frontend";
import {Center} from "@hosttools/frontend/components";
import {type AccountType} from "@hosttools/frontend/models/account";
import type React from "react";
import {memo, useCallback, useEffect, useState} from "react";
import {Route, Switch, useHistory, useRouteMatch} from "react-router-dom";

import ConnectedListings from "./ConnectedListings";
import ModalImportListings from "./ImportListings/ModalImportListings";
import SelectChannels from "./SelectChannels";

import Loader from "@/admin/components/Loader";
import useSearchQuery from "@/admin/hooks/useSearchQuery";
import ContentWrapper from "@/client/components/ContentWrapper";
import useCreateAirbnbAccount from "@/client/hooks/useCreateAirbnbAccount";

const AddListing: React.FC = () => {
    const {listings, isDemo, updateUser} = useUserContext();
    const history = useHistory();
    const {path} = useRouteMatch();

    const query = useSearchQuery();
    const onboarding = query.get("onboarding") || listings.length === 0;
    const redirectUrl = onboarding ? `${path}?onboarding=${onboarding}` : path;
    const returnUrl = query.get("returnUrl");
    const listingID = query.get("listingID") || undefined;

    const [accountType, setAccountType] = useState<AccountType>();

    const {accountID: airbnbAccountID, code} = useCreateAirbnbAccount();

    const hideModal = useCallback(() => {
        setAccountType(undefined);
    }, []);

    const handlePressChannel = useCallback((accountType: AccountType) => {
        setAccountType(accountType);
    }, []);

    const handleImportListingsSuccess = useCallback(async () => {
        if (isDemo) {
            await updateUser();
        }
        if (returnUrl) {
            history.push(returnUrl);
        } else {
            history.push(
                onboarding
                    ? `${path}/connected-listings?onboarding=true`
                    : `${path}/connected-listings`
            );
        }
        setAccountType(undefined);
    }, [history, path, onboarding, returnUrl, updateUser, isDemo]);

    const goBack = useCallback(() => {
        history.push(redirectUrl);
    }, [history, redirectUrl]);

    useEffect(() => {
        if (code && airbnbAccountID) {
            setAccountType("Airbnb");
        }
    }, [code, airbnbAccountID]);

    if (code && !airbnbAccountID) {
        return <Loader center>Creating account...</Loader>;
    }

    return (
        <ContentWrapper>
            <Center className="mb-5">
                <Switch>
                    <Route exact path={path}>
                        <SelectChannels
                            onAddListingManualSuccess={handleImportListingsSuccess}
                            onPressChannel={handlePressChannel}
                        />
                    </Route>
                    <Route path={`${path}/connected-listings`}>
                        <ConnectedListings goBack={goBack} />
                    </Route>
                </Switch>
            </Center>

            {accountType && (
                <ModalImportListings
                    accountType={accountType}
                    listingID={listingID}
                    oAuthReturnUrl={
                        typeof ONBOARDING_OAUTH_RETURN_URL !== "undefined"
                            ? ONBOARDING_OAUTH_RETURN_URL
                            : `${window.location.origin}/add-listing`
                    }
                    oAuthStateParameter={redirectUrl}
                    onClose={hideModal}
                    onImportListingsSuccess={handleImportListingsSuccess}
                />
            )}
        </ContentWrapper>
    );
};

export default memo(AddListing);
